import { CSSResultGroup, html, LitElement, nothing, PropertyValues } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
import { hostStyle } from '@cloudsuite/mosaic-shared/styles/host.style.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { rmaDetail } from './detail.style.js'
import { parseRmaDetailDTO, RmaDetailDTO } from 'components/ordering/rma/api/dtos/rma-detail-dto.js'
import { executeRmaRequest } from 'components/ordering/rma/api/executor.js'
import { GetRmaDetail } from 'components/ordering/rma/api/requests/get-rma-detail.js'
import { RmaLineDTO } from 'components/ordering/rma/api/dtos/rma-line-dto.js'
import { rmaBadgeVariant } from 'components/ordering/rma/components/shared/badge-variant.js'
import { uploadUrlContext } from 'components/ordering/rma/components/context/rma-context.js'
import 'components/discovery/product/image/image.js'

@customElement('csc-rma-detail')
export class CSCRmaDetail extends LitElement {
  static override styles: CSSResultGroup = [hostStyle, rmaDetail]

  @property({ attribute: 'rma', type: String })
  public rma = ''

  // Only used when the detail is fetched instead of injected; csc-rma passes the
  // id it read from the URL.
  @property({ attribute: 'rma-id', type: String })
  public rmaId = ''

  @property({ attribute: 'locale', type: String })
  public locale = 'en'

  @state()
  private detail: RmaDetailDTO | null = null

  // csc-rma wraps this in csc-rma-context, which provides the upload URL. A
  // server-rendered page that injects csc-rma-detail directly (see
  // CSCRma.hasServerRenderedContent) has no such ancestor, so the attribute
  // keeps file links working on its own.
  @property({ attribute: 'upload-url', type: String })
  public uploadUrlAttribute = ''

  @consume({ context: uploadUrlContext, subscribe: true })
  private uploadUrlContext = ''

  private get uploadUrl(): string {
    return this.uploadUrlAttribute || this.uploadUrlContext
  }

  protected override willUpdate(changed: PropertyValues): void {
    super.willUpdate(changed)
    if (!this.hasUpdated || changed.has('rma') || changed.has('rmaId')) {
      try {
        this.detail = this.rma ? parseRmaDetailDTO(JSON.parse(this.rma)) : null
      } catch (error) {
        console.error('[csc-rma-detail] failed to parse rma attribute:', error)
        this.detail = null
      }
      if (!this.rma && this.rmaId) void this.fetchDetail()
    }
  }

  // The server-rendered page injects the detail as the rma attribute. When it is
  // absent — csc-rma routing to the detail itself — read it from the API.
  private async fetchDetail(): Promise<void> {
    const result = await executeRmaRequest(new GetRmaDetail(this.rmaId))
    if (result instanceof RmaDetailDTO) this.detail = result
  }

  private formatDate(value: string | null): string {
    if (!value) return ''
    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return ''
    return new Intl.DateTimeFormat(this.locale, { dateStyle: 'long' }).format(date)
  }

  private renderHeader(detail: RmaDetailDTO) {
    const { header } = detail
    return html`
      <header class="rma-header">
        <cse-text as="h1" variant="heading-1" class="rma-title__heading">
          ${t('title', { ns: 'csc-rma-detail', default: 'RMA' })} ${header.number}
        </cse-text>
        <div class="rma-title">
          <cse-badge class="status" variant=${rmaBadgeVariant(header.state)}>${header.status}</cse-badge>
          <cse-text variant="caption-m" class="rma-title__updated">
            ${t('last_update', { ns: 'csc-rma-detail', default: 'Last update' })}:
            ${this.formatDate(header.lastUpdate ?? header.sent)}
          </cse-text>
        </div>
      </header>
    `
  }

  private renderProperties(detail: RmaDetailDTO) {
    const { header } = detail
    return html`
      <cse-paper variant="secondary">
        <div class="rma-properties" data-test="rma-properties">
          <cse-text-group>
            <cse-text slot="heading" variant="heading-4">${t('created', { ns: 'csc-rma-detail', default: 'Created' })}</cse-text>
            <cse-text variant="body">${this.formatDate(header.sent)}</cse-text>
          </cse-text-group>
          <div class="rma-properties__user">
            <cse-text-group>
              <cse-text slot="heading" variant="heading-4">${t('user', { ns: 'csc-rma-detail', default: 'User' })}</cse-text>
              <cse-text variant="body">${header.user}</cse-text>
            </cse-text-group>
            <cse-text variant="caption-m">${header.userEmail}</cse-text>
          </div>
          ${header.comment
            ? html`
                <cse-text-group data-test="rma-comment">
                  <cse-text slot="heading" variant="heading-4">${t('comment', { ns: 'csc-rma-detail', default: 'Comment' })}</cse-text>
                  <cse-text variant="body">${header.comment}</cse-text>
                </cse-text-group>
              `
            : nothing}
        </div>
      </cse-paper>
    `
  }

  private renderMemos(line: RmaLineDTO) {
    const memos: [string | null, ReturnType<typeof t>][] = [
      [line.memo, t('memo', { ns: 'csc-rma-detail', default: 'Serial number' })],
      [line.memo2, t('memo_two', { ns: 'csc-rma-detail', default: 'Comment' })],
      [line.memo3, t('memo_three', { ns: 'csc-rma-detail', default: 'Additional information' })],
    ]
    return memos
      .filter((entry): entry is [string, ReturnType<typeof t>] => Boolean(entry[0]))
      .map(([memo, label]) => this.renderField(label, memo))
  }

  private renderLineDetails(line: RmaLineDTO) {
    if (!line.shipmentDate && !line.customerOrderNr && !line.erpOrderNumber) return nothing
    return html`
      <cse-paper variant="secondary">
        <div class="rma-line__details">
          ${line.shipmentDate
            ? this.renderLineDetail(
                t('delivery_date', { ns: 'csc-rma-detail', default: 'Delivery date' }),
                this.formatDate(line.shipmentDate),
                'rma-line-delivery-date'
              )
            : nothing}
          ${line.customerOrderNr
            ? this.renderLineDetail(
                t('order_number', { ns: 'csc-rma-detail', default: 'Order number' }),
                line.customerOrderNr,
                'rma-line-order-number'
              )
            : nothing}
          ${line.erpOrderNumber
            ? this.renderLineDetail(
                t('erp_number', { ns: 'csc-rma-detail', default: 'ERP number' }),
                line.erpOrderNumber,
                'rma-line-erp-number'
              )
            : nothing}
        </div>
      </cse-paper>
    `
  }

  private renderLineDetail(label: ReturnType<typeof t>, value: string, testId: string) {
    return html`
      <cse-caption-description class="rma-line__detail" caption-variant="caption-s" description-variant="body-s">
        <span slot="caption">${label}</span>
        <span data-test=${testId}>${value}</span>
      </cse-caption-description>
    `
  }

  private renderField(label: ReturnType<typeof t>, value: string) {
    return html`
      <div class="rma-line__field">
        <cse-text variant="body" class="rma-line__field-label"><strong>${label}</strong></cse-text>
        <cse-text variant="body" class="rma-line__field-value">${value}</cse-text>
      </div>
    `
  }

  // The design shows the file type next to the name; derive it from the extension.
  private fileIcon(name: string): string {
    const extension = name.split('.').pop()?.toLowerCase() ?? ''
    if (extension === 'pdf') return 'file-pdf'
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) return 'file-image'
    return 'file'
  }

  private fileUrl(identifier: string): string {
    const base = this.uploadUrl.endsWith('/') ? this.uploadUrl : `${this.uploadUrl}/`
    return `${base}${identifier}`
  }

  private renderFiles(line: RmaLineDTO) {
    if (!line.pictures.length) return nothing
    return html`
      <div class="rma-line__files">
        <cse-text variant="body" class="rma-line__files-label">
          <strong>${t('files', { ns: 'csc-rma-detail', default: 'Files' })}</strong>
        </cse-text>
        <cse-list class="rma-line__pictures">
          ${line.pictures.map((picture) => html`
            <cse-list-item variant="unstyled">
              <cse-link url="${this.fileUrl(picture.identifier)}" target="_blank">
                <cse-icon slot="prefix" icon-name=${this.fileIcon(picture.name)} icon-style="light"></cse-icon>
                ${picture.name}
              </cse-link>
            </cse-list-item>
          `)}
        </cse-list>
      </div>
    `
  }

  private renderLine(line: RmaLineDTO) {
    const memos = this.renderMemos(line)
    return html`
      <cse-paper variant="primary" bordered class="rma-line" data-test="rma-line">
        <div class="rma-line__header">
          <csc-product-image
            class="rma-line__image"
            source=${line.imageUrl}
            alt=${line.productName}
          ></csc-product-image>
          <div class="rma-line__info">
            <cse-text variant="product-title-m" class="rma-line__name">${line.productName}</cse-text>
            <cse-text variant="caption-m" class="rma-line__code">
              <strong>${t('product_number', { ns: 'csc-rma-detail', default: 'Product number' })}</strong>:
              ${line.productCode}${line.ean
                ? html` | <strong>${t('ean', { ns: 'csc-rma-detail', default: 'EAN' })}</strong>: ${line.ean}`
                : nothing}
            </cse-text>
          </div>
          <cse-text variant="body" class="rma-line__quantity">${line.quantity}x</cse-text>
        </div>
        ${this.renderLineDetails(line)}
        <cse-divider orientation="horizontal"></cse-divider>
        <div class="rma-line__fields">
          ${this.renderField(t('reason', { ns: 'csc-rma-detail', default: 'Reason' }), line.reason)}
          ${line.oem ? this.renderField(t('oem', { ns: 'csc-rma-detail', default: 'OEM' }), line.oem) : nothing}
          ${line.saleReference
            ? this.renderField(t('sale_reference', { ns: 'csc-rma-detail', default: 'Sale reference' }), line.saleReference)
            : nothing}
          ${line.shipmentReference
            ? this.renderField(t('shipment_reference', { ns: 'csc-rma-detail', default: 'Shipment reference' }), line.shipmentReference)
            : nothing}
          ${memos}
        </div>
        ${this.renderFiles(line)}
      </cse-paper>
    `
  }

  private renderLines(detail: RmaDetailDTO) {
    if (!detail.lines.length) {
      return html`
        <cse-text variant="body" class="rma-empty" data-test="rma-no-lines">
          ${t('no_lines', { ns: 'csc-rma-detail', default: 'No items in this return request.' })}
        </cse-text>
      `
    }
    return html`<cse-list class="rma-lines">${detail.lines.map((line) => this.renderLine(line))}</cse-list>`
  }

  override render() {
    if (!this.detail) return html`<div class="rma-detail" data-test="rma-detail"></div>`
    return html`
      <div class="rma-detail" data-test="rma-detail">
        ${this.renderHeader(this.detail)}
        <div class="rma-detail__content">
          <div class="rma-detail__lines">${this.renderLines(this.detail)}</div>
          <aside class="rma-detail__aside">${this.renderProperties(this.detail)}</aside>
        </div>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'csc-rma-detail': CSCRmaDetail
  }
}
